import React from 'react';
import { CheckCircleIcon, DocumentTextIcon, PhotoIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { jsPDF } from 'jspdf';

export default function VIPEnrolledView({ vipStatus, listing, readOnly = false, basePath = null }) {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const computedBasePath = basePath || `/${user.role}`;
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadAgreement = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = 20;

    const checkNewPage = (requiredSpace = 15) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    const addText = (text, fontSize = 10, font = 'normal', leftMargin = 0) => {
      doc.setFontSize(fontSize);
      doc.setFont(undefined, font);
      const lines = doc.splitTextToSize(text, maxWidth - leftMargin);
      checkNewPage(lines.length * (fontSize / 2 + 2));
      doc.text(lines, margin + leftMargin, yPosition);
      yPosition += lines.length * (fontSize / 2 + 2);
    };

    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('VinoSeeker VIP Partner Agreement', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 12;

    // Signature Information Box
    doc.setDrawColor(100);
    doc.setFillColor(245, 245, 245);
    checkNewPage(35);
    const boxHeight = 33;
    doc.rect(margin, yPosition, maxWidth, boxHeight, 'FD');
    yPosition += 7;

    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Agreement Details:', margin + 5, yPosition);
    yPosition += 6;

    doc.setFont(undefined, 'normal');
    doc.text(`Winery: ${listing.name}`, margin + 5, yPosition);
    yPosition += 5;

    if (vipStatus.acceptedBy) {
      doc.text(`Signed by: ${vipStatus.acceptedBy.name} (${vipStatus.acceptedBy.email})`, margin + 5, yPosition);
      yPosition += 5;
    }

    doc.text(`Date Joined: ${formatDate(vipStatus.vipJoinedDate)}`, margin + 5, yPosition);
    yPosition += 5;

    if (vipStatus.vipAgreementAcceptedDate) {
      doc.text(`Agreement Accepted: ${formatDateTime(vipStatus.vipAgreementAcceptedDate)}`, margin + 5, yPosition);
    }
    yPosition += 15;

    // Purpose Section
    checkNewPage();
    addText('Purpose:', 11, 'bold');
    yPosition += 2;
    addText('This agreement allows your winery to participate in the VinoSeeker VIP Network and offer benefits to VinoSeeker VIP Members.');
    yPosition += 5;

    // Winery Agrees To Section
    doc.setDrawColor(200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    addText('Winery Agrees To:', 12, 'bold');
    yPosition += 3;

    addText('1. Offer VIP Benefits:', 10, 'bold');
    yPosition += 2;
    addText('Provide the following benefit(s) to VinoSeeker VIP Members who present valid membership credentials:', 10, 'normal', 5);
    addText('• Buy-one-get-one tastings', 10, 'normal', 10);
    addText('• 10% off bottle purchases', 10, 'normal', 10);
    yPosition += 3;

    addText('2. Honor Benefits Consistently:', 10, 'bold');
    yPosition += 2;
    addText('VIP benefits must be honored during regular tasting room hours, subject only to capacity limitations or appointment requirements that apply to all guests.', 10, 'normal', 5);
    yPosition += 3;

    addText('3. Minimum Commitment:', 10, 'bold');
    yPosition += 2;
    addText('Participate in the VIP Network for at least 6 months from enrollment date.', 10, 'normal', 5);
    yPosition += 3;

    addText('4. Usage Limits:', 10, 'bold');
    yPosition += 2;
    addText('VIP members may redeem benefits at your winery once per 6-month period.', 10, 'normal', 5);
    yPosition += 3;

    addText('5. Verification:', 10, 'bold');
    yPosition += 2;
    addText('Staff will verify VIP membership via the VinoSeeker app before applying benefits.', 10, 'normal', 5);
    yPosition += 5;

    // VinoSeeker Agrees To Section
    doc.setDrawColor(200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    addText('VinoSeeker Agrees To:', 12, 'bold');
    yPosition += 3;

    addText('1. Promote Your Winery:', 10, 'bold');
    yPosition += 2;
    addText('Display a VIP badge on your listing and include you in VIP winery filters.', 10, 'normal', 5);
    yPosition += 3;

    addText('2. Drive Qualified Traffic:', 10, 'bold');
    yPosition += 2;
    addText('Market the VIP program to wine enthusiasts who are active winery visitors.', 10, 'normal', 5);
    yPosition += 3;

    addText('3. Provide Tools:', 10, 'bold');
    yPosition += 2;
    addText('Supply QR codes, promotional materials, and redemption tracking.', 10, 'normal', 5);
    yPosition += 5;

    // Terms Section
    doc.setDrawColor(200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    addText('Terms:', 12, 'bold');
    yPosition += 3;

    addText('No Cost: Participation in the VIP Network is free.', 10, 'normal', 5);
    addText('Opt-Out: Either party may terminate participation with 30 days written notice.', 10, 'normal', 5);
    addText('Modification: VIP benefits may be modified with 30 days notice to VinoSeeker.', 10, 'normal', 5);
    addText('No Guarantee: VinoSeeker makes no guarantee of visitor volume.', 10, 'normal', 5);
    yPosition += 5;

    // Data & Privacy Section
    doc.setDrawColor(200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    addText('Data & Privacy:', 12, 'bold');
    yPosition += 3;

    addText('• Redemption data (date, time, user name) will be shared with winery for verification purposes only.', 10, 'normal', 5);
    addText('• User contact information remains private unless user opts into winery\'s mailing list.', 10, 'normal', 5);
    yPosition += 5;

    // Footer
    doc.setDrawColor(200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    doc.setFontSize(9);
    doc.setTextColor(100);
    addText('By checking the agreement box and clicking "Join VIP Network," you confirm that you are authorized to bind your winery to this agreement.');
    yPosition += 3;
    addText('Last updated: October 2025');
    yPosition += 8;

    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('VinoSeeker VIP Network - service@vinoseeker.com', pageWidth / 2, yPosition, { align: 'center' });

    // Save the PDF
    const fileName = `VIP-Agreement-${listing.name.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const handlePromotionalMaterials = () => {
    navigate(`${computedBasePath}/listings/${listing.id}/promotions`);
  };

  return (
    <div className="space-y-6">
      {/* Status Section */}
      <div className="bg-[#7E9B0C]/10 border border-[#7E9B0C] rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircleIcon className="h-8 w-8 text-[#7E9B0C]" />
          <h2 className="text-2xl font-bold text-gray-900">VIP Network Active</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Joined:</span>
            <span className="ml-2 font-medium text-gray-900">{formatDate(vipStatus.vipJoinedDate)}</span>
          </div>
          {vipStatus.acceptedBy && (
            <div>
              <span className="text-gray-600">Agreement accepted by:</span>
              <span className="ml-2 font-medium text-gray-900">{vipStatus.acceptedBy.name}</span>
            </div>
          )}
          {vipStatus.vipAgreementAcceptedDate && (
            <div className="md:col-span-2">
              <span className="text-gray-600">Agreement accepted on:</span>
              <span className="ml-2 font-medium text-gray-900">{formatDateTime(vipStatus.vipAgreementAcceptedDate)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Your VIP Offer */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your VIP Offer</h3>
        <div className="bg-gray-50 rounded-md p-4">
          <p className="font-medium text-gray-900 mb-2">VIP Member Benefits:</p>
          <ul className="space-y-1 text-gray-700">
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-[#7E9B0C]" />
              Buy-one-get-one tastings
            </li>
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-[#7E9B0C]" />
              10% off bottle purchases
            </li>
          </ul>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">How Your Listing Appears</h3>
        <div className="bg-gray-50 rounded-md p-4 border-2 border-dashed border-gray-300">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#7E9B0C] text-white text-xs font-semibold">
              <CheckCircleIcon className="h-4 w-4" />
              VIP
            </span>
            <span className="text-sm text-gray-600">VIP badge appears on your listing</span>
          </div>
          <p className="text-xs text-gray-500">
            Your listing now displays a VIP badge on search results, detail pages, and map markers.
          </p>
        </div>
      </div>

      {/* Resources Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Resources</h3>
        <div className="space-y-3">
          <button
            onClick={handleDownloadAgreement}
            className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 transition-colors w-full text-left"
          >
            <DocumentTextIcon className="h-6 w-6 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900">Download VIP Partner Agreement (PDF)</p>
              <p className="text-sm text-gray-600">Keep a copy for your records</p>
            </div>
          </button>
          <button
            onClick={handlePromotionalMaterials}
            className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 transition-colors w-full text-left"
          >
            <PhotoIcon className="h-6 w-6 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900">Manage Promotional Materials</p>
              <p className="text-sm text-gray-600">View and manage QR codes, flights, and promotions</p>
            </div>
          </button>
          <div className="flex items-center gap-3 p-3 rounded-md bg-gray-50 opacity-60 cursor-not-allowed">
            <QuestionMarkCircleIcon className="h-6 w-6 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900">How to Verify VIP Members <span className="text-sm font-normal text-gray-500">(Coming Soon)</span></p>
              <p className="text-sm text-gray-600">Step-by-step verification guide</p>
            </div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
        <p className="text-gray-700 mb-4">
          To pause or cancel your VIP Network participation, please contact us at{' '}
          <a href="mailto:service@vinoseeker.com" className="text-[#630801] hover:text-[#7E9B0C] font-medium">
            service@vinoseeker.com
          </a>
        </p>
        <p className="text-sm text-gray-600">We typically respond within 24 hours.</p>
      </div>
    </div>
  );
}
